import React from "react";
import { Card, CardContent, Typography, Box, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import TimelineIcon from "@mui/icons-material/Timeline";
import AppleIcon from "@mui/icons-material/Apple";
import ShopIcon from "@mui/icons-material/Shop";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptIcon from "@mui/icons-material/Receipt";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import BugReportIcon from "@mui/icons-material/BugReport";
import SpeedIcon from "@mui/icons-material/Speed";
import DownloadIcon from "@mui/icons-material/Download";
import AssessmentIcon from "@mui/icons-material/Assessment";
import RecyclingIcon from "@mui/icons-material/Recycling";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import InventoryIcon from "@mui/icons-material/Inventory";
import PercentIcon from "@mui/icons-material/Percent";
import StoreIcon from "@mui/icons-material/Store";

const getIconFromName = (name) => {
  if (!name) return <AssessmentIcon />;
  const n = name.toLowerCase();
  if (n.includes("recycl")) return <RecyclingIcon />;
  if (n.includes("voucher") || n.includes("reward") || n.includes("point") || n.includes("gift")) return <CardGiftcardIcon />;
  if (n.includes("item") || n.includes("collect") || n.includes("inventory")) return <InventoryIcon />;
  if (n.includes("rate") || n.includes("percentage") || n.includes("percent") || n.includes("ratio") || n.includes("conversion")) return <PercentIcon />;
  if (n.includes("branch") || n.includes("location") || n.includes("store")) return <StoreIcon />;
  if (n.includes("revenue")) return <AttachMoneyIcon />;
  if (n.includes("transaction")) return <ReceiptIcon />;
  if (n.includes("ios") || n.includes("apple")) return <AppleIcon />;
  if (n.includes("android") || n.includes("play")) return <ShopIcon />;
  if (n.includes("new registered") || n.includes("new app user") || n.includes("new user") || n.includes("installers")) return <PersonAddIcon />;
  if (n.includes("user") || n.includes("registered") || n.includes("client")) return <PeopleIcon />;
  if (n.includes("engaged")) return <TouchAppIcon />;
  if (n.includes("session") || n.includes("cohort") || n.includes("activation")) return <TimelineIcon />;
  if (n.includes("download") || n.includes("install")) return <DownloadIcon />;
  if (n.includes("crash") || n.includes("anr") || n.includes("error") || n.includes("defect") || n.includes("issue")) return <BugReportIcon />;
  if (n.includes("speed") || n.includes("time") || n.includes("duration") || n.includes("latency")) return <SpeedIcon />;
  return <AssessmentIcon />;
};

function formatValue(v) {
  if (v === null || v === undefined) return "N/A";
  if (typeof v === "number") {
    if (Number.isInteger(v)) {
      return v.toLocaleString();
    }
    // Float - format up to 2 decimal places
    return Number(v.toFixed(2)).toLocaleString();
  }
  return String(v);
}

export default function MetricCard({ icon, name, value, unit, availability, comparison, source, tooltip }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Check if value is a structured metric object: { available: boolean, value: any, unit?: string, reason?: string }
  const isMetricObject = value !== null && typeof value === "object" && ("available" in value || ("value" in value && !("ios" in value || "android" in value)));
  const isDictionaryBreakdown = value !== null && typeof value === "object" && !isMetricObject;

  let displayValue = "N/A";
  let activeUnit = unit === "percent" ? "%" : unit;
  let isPartial = availability?.is_partial;
  let isEstimated = availability?.is_estimated;
  let note = tooltip || availability?.coverage_note || availability?.reason;

  if (isMetricObject) {
    const isAvail = value.available !== false && value.value !== null && value.value !== undefined;
    displayValue = isAvail ? formatValue(value.value) : "N/A";
    if (value.unit) {
      activeUnit = value.unit === "percent" ? "%" : value.unit;
    }
    if (value.reason) {
      note = value.reason;
    }
    if (value.is_partial) isPartial = true;
    if (value.is_estimated) isEstimated = true;
  } else if (!isDictionaryBreakdown) {
    displayValue = formatValue(value);
  }

  if (isEstimated && displayValue !== "N/A" && !isDictionaryBreakdown) {
    displayValue = `~${displayValue}`;
  }

  const renderComparison = () => {
    if (!comparison || comparison.change_pct == null) return null;
    const isUp = comparison.change_pct >= 0;
    const color = isUp ? "#34d399" : "#f87171";
    const bg = isUp ? "rgba(52,211,153,0.1)" : "rgba(248,113,113,0.1)";
    return (
      <Box 
        display="flex" 
        alignItems="center" 
        style={{
          background: bg,
          color: color,
          padding: "2px 8px",
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: 600,
          marginLeft: "auto"
        }}
      >
        {isUp ? <ArrowUpwardIcon style={{ fontSize: 14, marginRight: 2 }} /> : <ArrowDownwardIcon style={{ fontSize: 14, marginRight: 2 }} />}
        {Math.abs(comparison.change_pct).toFixed(1)}%
      </Box>
    );
  };

  return (
    <Card style={{ height: "100%", display: "flex", flexDirection: "column", background: isDark ? "linear-gradient(145deg, #1e2128 0%, #262a33 100%)" : "#fff", border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.08)", color: isDark ? "#fff" : "#333", borderRadius: "14px", boxShadow: isDark ? "none" : "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)" }}>
      <CardContent style={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "20px" }}>
        <Box display="flex" alignItems="center" mb={2}>
          {(icon || getIconFromName(name)) && (
            <Box
              style={{
                backgroundColor: "rgba(99,102,241,0.12)",
                borderRadius: "50%",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "12px",
                color: "#818cf8"
              }}
            >
              {icon || getIconFromName(name)}
            </Box>
          )}
          <Box display="flex" flexDirection="column" flexGrow={1}>
            <Typography variant="subtitle2" style={{ fontWeight: 500, color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.5)", textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "12px", lineHeight: 1.2 }}>
              {name}
            </Typography>
            {source && (
              <Typography variant="caption" style={{ color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.4)", fontSize: "10px" }}>
                {source}
              </Typography>
            )}
          </Box>
        </Box>
        <Box display="flex" alignItems="baseline" flexWrap="wrap">
          {isDictionaryBreakdown ? (
            <Box display="flex" flexDirection="column" gap={0.5} width="100%" mt={1}>
              {Object.entries(value).map(([k, v]) => (
                <Box key={k} display="flex" justifyContent="space-between" alignItems="center" borderBottom={isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)"} pb={0.5}>
                  <Typography variant="body2" style={{ color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)", textTransform: "capitalize", fontSize: "12px" }}>
                    {k.replace(/_/g, " ")}
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 600, color: isDark ? "#fff" : "#333", fontSize: "14px" }}>
                    {isEstimated ? `~${v}` : v} {activeUnit && <span style={{ fontWeight: 400, color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", fontSize: "10px", marginLeft: 2 }}>{activeUnit}</span>}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <>
              <Typography variant="h4" component="span" style={{ fontWeight: 700, color: isDark ? "#fff" : "#333", fontSize: "28px", lineHeight: 1.1 }}>
                {displayValue}
              </Typography>
              {activeUnit && displayValue !== "N/A" && (
                <Typography variant="body2" style={{ marginLeft: "4px", color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)" }}>
                  {activeUnit}
                </Typography>
              )}
            </>
          )}
          
          {/* Availability Indicators & Tooltip */}
          {(isPartial || note) && (
            <Box ml={1} display="flex" alignItems="center" alignSelf={isDictionaryBreakdown ? "flex-start" : "center"} mt={isDictionaryBreakdown ? 1 : 0}>
              {isPartial && (
                <Tooltip title={note || "Partial data available for this period"}>
                  <WarningAmberIcon style={{ fontSize: 16, color: "#fbbf24", cursor: "help" }} />
                </Tooltip>
              )}
              {note && !isPartial && (
                <Tooltip title={note}>
                  <InfoOutlinedIcon style={{ fontSize: 16, color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", cursor: "help", marginLeft: 4 }} />
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
        <Box display="flex" alignItems="center" mt={1}>
          {renderComparison()}
        </Box>
      </CardContent>
    </Card>
  );
}
